/**
 * Feature Flags Utility Functions
 *
 * Provides helper functions for persisting the feature flags store state to
 * localStorage and for synchronizing it across tabs via the 'storage' event.
 * Mirrors the relevant parts of `auth.utils.ts`.
 *
 * See spec 030 — internal-contracts.md § 4.4.
 */

import type {
  FeatureFlagConfig,
  FrontendFeatureFlag,
} from '@openbox/shared-types/lib/featureFlags'

// LocalStorage key for the persisted feature flags store state
export const FEATURE_FLAGS_STORAGE_KEY = 'feature_flags_state'

/**
 * Serializable shape of the feature flags store state that is persisted to
 * localStorage and broadcast to other tabs.
 */
export type PersistedFeatureFlagsState = {
  flags: Record<string, FrontendFeatureFlag>
  loadedForCompanyId: string | null
}

/**
 * Persist the feature flags store state to localStorage.
 *
 * Writing to localStorage triggers a 'storage' event in all OTHER tabs, which
 * is how cross-tab synchronization works (D9).
 *
 * @param state - The serializable store state to persist
 */
export function saveFeatureFlagsState(state: PersistedFeatureFlagsState): void {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Failed to persist feature flags state:', error)
  }
}

/**
 * Read the persisted feature flags store state from localStorage.
 *
 * @returns The parsed state, or null if not present or invalid
 */
export function readFeatureFlagsState(): PersistedFeatureFlagsState | null {
  if (!import.meta.client) {
    return null
  }
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY)
    if (!raw) {
      return null
    }
    return parseFeatureFlagsState(raw)
  } catch (error) {
    console.error('Failed to read feature flags state:', error)
    return null
  }
}

/**
 * Remove the persisted feature flags store state from localStorage.
 *
 * Called on logout (clearState) — also broadcasts a 'storage' event with
 * `newValue === null` to other tabs.
 */
export function clearFeatureFlagsState(): void {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear feature flags state:', error)
  }
}

/**
 * Parse a serialized feature flags state string into a typed object.
 *
 * Returns null when the payload is missing, malformed, or doesn't match the
 * expected shape (defensive — never trust cross-tab payloads blindly).
 *
 * @param raw - The serialized JSON string
 */
export function parseFeatureFlagsState(
  raw: string | null,
): PersistedFeatureFlagsState | null {
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as unknown

    if (!isPlainObject(parsed)) {
      return null
    }

    // `flags` must be a plain object (a map of key -> flag). Arrays satisfy
    // `typeof x === 'object'`, so they have to be rejected explicitly — otherwise
    // an unexpected shape could be reinjected into the store via localStorage /
    // the cross-tab `storage` event.
    if (!isPlainObject(parsed.flags)) {
      return null
    }

    const loadedForCompanyId = parsed.loadedForCompanyId
    return {
      flags: parsed.flags as Record<string, FrontendFeatureFlag>,
      loadedForCompanyId:
        typeof loadedForCompanyId === 'string' ? loadedForCompanyId : null,
    }
  } catch {
    return null
  }
}

/**
 * Whether `value` is a non-null plain object (i.e. `typeof === 'object'` and not
 * an array). Used to defensively validate cross-tab / localStorage payloads.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Callbacks for the feature flags storage sync handler.
 */
export type FeatureFlagsStorageSyncCallbacks = {
  /** Called with the new state when another tab updated the feature flags. */
  onFlagsChange: (state: PersistedFeatureFlagsState) => void
}

/**
 * Create a 'storage' event handler for cross-tab feature flags synchronization.
 *
 * When another tab refetches the flags and persists the new state via
 * {@link saveFeatureFlagsState}, this handler fires in the current tab and
 * invokes `onFlagsChange` with the deserialized state. A `newValue === null`
 * event (the other tab cleared the state, e.g. on logout) is ignored — auth's
 * own storage-sync handler is responsible for the logout path.
 *
 * Note: The 'storage' event only fires when localStorage is changed by ANOTHER
 * window/tab, not the current one.
 *
 * @param callbacks - Object containing the onFlagsChange callback
 * @returns A storage event handler function to be used with addEventListener
 */
export function createFeatureFlagsStorageSyncHandler(
  callbacks: FeatureFlagsStorageSyncCallbacks,
): (event: StorageEvent) => void {
  return (event: StorageEvent) => {
    if (event.key !== FEATURE_FLAGS_STORAGE_KEY) {
      return
    }

    // State removed in another tab (logout / cleared) — nothing to reinject.
    if (!event.newValue) {
      return
    }

    const state = parseFeatureFlagsState(event.newValue)
    if (state) {
      callbacks.onFlagsChange(state)
    }
  }
}

export type { FeatureFlagConfig, FrontendFeatureFlag }
